import { forwardRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from './cn';
import { useT } from './i18n';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; size?: 'sm' | 'md' };
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({ variant = 'secondary', size = 'md', className, type = 'button', ...props }, ref) => (
  <button ref={ref} type={type} className={cn(
    'inline-flex items-center justify-center gap-1.5 rounded-[var(--radius-control)] font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-50',
    size === 'sm' ? 'h-7 px-2.5 text-sm' : 'h-8 px-3 text-base',
    variant === 'primary' && 'bg-boundary text-boundary-ink hover:bg-boundary/90',
    variant === 'secondary' && 'border border-rule-strong bg-surface hover:bg-sunken',
    variant === 'ghost' && 'text-graphite hover:bg-sunken hover:text-ink',
    variant === 'danger' && 'border border-deny/40 bg-surface text-deny hover:bg-deny/8',
    className)} {...props} />
));

export function Kbd({ children }: { children: ReactNode }) {
  // Hidden on small screens: touch users have no keyboard to use it with.
  return <kbd className="hidden rounded border sm:inline border-rule-strong bg-surface px-1 font-mono text-xs text-graphite">{children}</kbd>;
}

/*
 * Layout scale (keep every page on it):
 *   page width  max-w-6xl, left edge identical on every page
 *   between page sections        gap-6 (24px)
 *   panel inset / list row       px-4, rows py-3; setting rows py-4
 *   page header → first section  mb-6; section header → content mb-4
 */
export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('mx-auto grid w-full max-w-6xl content-start gap-6', className)}>{children}</div>;
}

/** A titled region of a page. Panels are for grouping, not decoration: no shadow, one radius. */
export function Panel({ title, description, actions, footer, children, className }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; footer?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <section className={cn('min-w-0 rounded-[var(--radius-panel)] border border-rule bg-surface', className)}>
      {(title || actions) && (
        // Actions stack under the title on narrow screens so descriptions keep a readable measure.
        <header className="flex flex-col gap-3 border-b border-rule px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">{title && <h2 className="text-lg font-semibold">{title}</h2>}{description && <p className="mt-0.5 max-w-[72ch] text-sm text-graphite">{description}</p>}</div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      {children}
      {footer && <div className="border-t border-rule px-4 py-3">{footer}</div>}
    </section>
  );
}
/** One entry in a panel list. */
export const rowClass = 'border-b border-rule px-4 py-3 last:border-b-0';

/** Page title. Actions align with the title line, not the description. */
export function PageHeader({ title, description, actions }: { title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0"><h1 className="text-2xl font-semibold tracking-[-0.01em]">{title}</h1>{description && <p className="mt-1 max-w-[70ch] text-graphite">{description}</p>}</div>
      {actions && <div className="flex shrink-0 items-center gap-2 sm:pt-0.5">{actions}</div>}
    </header>
  );
}
/** Heading for a section inside a page that already has a title (e.g. inside a project). */
export function SectionHeader({ title, description, actions }: { title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0"><h2 className="text-xl font-semibold">{title}</h2>{description && <p className="mt-1 max-w-[70ch] text-graphite">{description}</p>}</div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/**
 * A setting: what it is on the left, its control on the right in a fixed-width column so controls
 * line up down a panel. Stacks on narrow screens.
 */
export function SettingRow({ label, htmlFor, description, meta, control, wide = false }: { label: ReactNode; htmlFor?: string; description?: ReactNode; meta?: ReactNode; control: ReactNode; wide?: boolean }) {
  return (
    <div className={cn('grid gap-3 border-b border-rule px-4 py-4 last:border-b-0 sm:items-center sm:gap-8', wide ? 'sm:grid-cols-[minmax(0,1fr)_20rem]' : 'sm:grid-cols-[minmax(0,1fr)_14rem]')}>
      <div className="min-w-0">
        {htmlFor ? <label htmlFor={htmlFor} className="font-medium">{label}</label> : <p className="font-medium">{label}</p>}
        {description && <p className="mt-0.5 max-w-[60ch] text-sm text-graphite">{description}</p>}
        {meta && <div className="mt-1.5 text-sm">{meta}</div>}
      </div>
      <div className="min-w-0 sm:justify-self-end sm:w-full">{control}</div>
    </div>
  );
}

/**
 * Data table inside a panel. Scrolls horizontally on its own instead of widening the page.
 * Columns: `{ label, className }` — use `num` for numbers and `action` for trailing buttons.
 */
export const col = { num: 'text-right tabular-nums', action: 'w-px whitespace-nowrap text-right', hideSm: 'hidden sm:table-cell', hideMd: 'hidden md:table-cell' };
export function Table({ head, children, minWidth = '36rem' }: { head: { label?: ReactNode; className?: string }[]; children: ReactNode; minWidth?: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left [&_td]:px-4 [&_td]:py-3 [&_td]:align-top [&_tbody_tr]:border-b [&_tbody_tr]:border-rule [&_tbody_tr:last-child]:border-b-0" style={{ minWidth }}>
        <thead className="text-sm text-graphite"><tr className="border-b border-rule">{head.map((h, i) => <th key={i} scope="col" className={cn('px-4 py-2 font-normal', h.className)}>{h.label}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Field({ label, hint, children, htmlFor }: { label: ReactNode; hint?: ReactNode; children: ReactNode; htmlFor?: string }) {
  return (
    // content-start: in a multi-column form the row stretches to its tallest field; without it the extra
    // height is spread between label and input, so neighbouring inputs stop lining up.
    <div className="grid content-start gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium">{label}</label>
      {children}
      {hint && <p className="text-sm text-graphite">{hint}</p>}
    </div>
  );
}
const control = 'h-8 w-full rounded-[var(--radius-control)] border border-rule-strong bg-surface px-2.5 text-base placeholder:text-graphite/70 focus-visible:outline-2 focus-visible:outline-boundary focus-visible:outline-offset-0';
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...p }, ref) => <input ref={ref} className={cn(control, className)} {...p} />);
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(({ className, ...p }, ref) => <select ref={ref} className={cn(control, 'pr-7', className)} {...p} />);

/** Machine identifiers (hashes, locators, ids). Truncated in the middle; full value on hover and copy. */
export function Mono({ value, head = 12, tail = 6, copy = false, className }: { value: string; head?: number; tail?: number; copy?: boolean; className?: string }) {
  const [copied, setCopied] = useState(false);
  const t = useT();
  const short = value.length > head + tail + 1 ? `${value.slice(0, head)}…${value.slice(-tail)}` : value;
  return (
    <span className={cn('inline-flex items-center gap-1 font-mono text-sm', className)} title={value}>
      <span className="truncate">{short}</span>
      {copy && (
        <button type="button" aria-label={t.common.copy(value)} className="text-graphite hover:text-ink" onClick={() => { void navigator.clipboard.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1_200); }); }}>
          {copied ? <Check size={13} /> : <Copy size={13} />}
        </button>
      )}
    </span>
  );
}

/** A command to run as written: its own line, wrapped only between arguments, copied whole from the right. */
export function Command({ children }: { children: string }) {
  const [copied, setCopied] = useState(false);
  const t = useT();
  const tokens = children.match(/"[^"]*"|\S+/g) ?? [];
  return (
    <div className="mt-2 flex items-start gap-2 rounded-[var(--radius-control)] border border-rule bg-sunken py-1.5 pr-1.5 pl-3">
      <code className="min-w-0 flex-1 py-0.5 text-sm leading-6 text-ink">
        {tokens.map((token, i) => <span key={i}>{i > 0 && ' '}<span className="whitespace-nowrap">{token}</span></span>)}
      </code>
      <button type="button" aria-label={copied ? t.common.copied : t.common.copyCommand} title={copied ? t.common.copied : t.common.copyCommand}
        className="grid size-7 shrink-0 place-items-center rounded-[var(--radius-control)] text-graphite hover:bg-rule/60 hover:text-ink"
        onClick={() => { void navigator.clipboard.writeText(children).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1_200); }); }}>
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="grid justify-items-start gap-2 px-4 py-8">
      <p className="font-medium">{title}</p>
      {children && <div className="max-w-[60ch] text-graphite">{children}</div>}
      {action}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('animate-pulse rounded bg-sunken', className)} />;
}

/** Definition list for "label: value" facts. Keeps alignment consistent across detail pages. */
export function Facts({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[minmax(7rem,max-content)_minmax(0,1fr)] gap-x-6 gap-y-2">
      {items.map(([k, v], i) => [<dt key={`k${i}`} className="text-graphite">{k}</dt>, <dd key={`v${i}`} className="min-w-0">{v}</dd>])}
    </dl>
  );
}
